import { PenTool, PenLine, Calendar, Type, CheckSquare, AlertCircle } from 'lucide-react';

const FIELD_KINDS = [
  { kind: 'signature', label: 'Signature', Icon: PenTool },
  { kind: 'initials', label: 'Initials', Icon: PenLine },
  { kind: 'date', label: 'Date', Icon: Calendar },
  { kind: 'text', label: 'Text', Icon: Type },
  { kind: 'checkbox', label: 'Checkbox', Icon: CheckSquare },
];

const ROLE_LABEL = { cc: 'CC', approver: 'Approver', signer: 'Signer' };

/**
 * @param {object} props
 * @param {Array} props.recipients
 * @param {string|null} props.activeRecipientId
 * @param {(id: string) => void} props.onRecipientChange
 * @param {string|null} props.activeFieldKind
 * @param {(kind: string|null) => void} props.onFieldKindChange
 * @param {Record<string, number>} [props.signerFieldCounts] — required-field count per signer id
 */
export default function FieldPalette({
  recipients,
  activeRecipientId,
  onRecipientChange,
  activeFieldKind,
  onFieldKindChange,
  signerFieldCounts = {},
}) {
  const activeRecipient = recipients.find((r) => r.id === activeRecipientId);
  const activeColor = activeRecipient?.color || '#6366F1';

  return (
    <div className="space-y-5">
      {/* Recipient switcher — replaces the old <select> so the sender sees
          every recipient and their assignment state at a glance. Inactive
          signers with zero required fields get an amber warning so the
          envelope can't silently advance into a server-rejected state. */}
      <div>
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
          Placing for
        </label>
        <div className="space-y-1.5">
          {recipients.map((r) => {
            const isActive = r.id === activeRecipientId;
            const isSigner = r.role === 'signer';
            const count = isSigner ? (signerFieldCounts[r.id] || 0) : 0;
            const isZeroFieldSigner = isSigner && count === 0;
            const showAmber = !isActive && isZeroFieldSigner;

            let classes = 'w-full flex items-start gap-2 px-2.5 py-2 rounded-lg text-left transition border';
            if (isActive) {
              classes += ' border-transparent';
            } else if (showAmber) {
              classes += ' border-amber-300 bg-amber-50/60 hover:bg-amber-50';
            } else {
              classes += ' border-gray-200 hover:bg-gray-50';
            }

            const inlineStyle = isActive
              ? {
                  borderLeftWidth: 2,
                  borderLeftColor: r.color,
                  backgroundColor: `${r.color}14`,
                }
              : undefined;

            const countText = isSigner
              ? count === 0
                ? 'No fields yet'
                : `${count} required`
              : null;

            return (
              <button
                key={r.id}
                type="button"
                onClick={() => onRecipientChange(r.id)}
                className={classes}
                style={inlineStyle}
                title={isZeroFieldSigner ? 'No required fields placed yet' : undefined}
              >
                <div
                  className="w-2.5 h-2.5 rounded-full mt-1.5 shrink-0"
                  style={{ backgroundColor: r.color }}
                />
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-1.5">
                    <span className="text-sm font-medium text-gray-900 truncate">
                      {r.name || r.email || 'Unnamed'}
                    </span>
                    {!isSigner && (
                      <span className="text-[10px] font-semibold px-1.5 py-0.5 rounded bg-gray-100 text-gray-600 uppercase tracking-wide shrink-0">
                        {ROLE_LABEL[r.role] || r.role}
                      </span>
                    )}
                  </div>
                  {r.email && (
                    <div className="text-xs text-gray-500 truncate">{r.email}</div>
                  )}
                </div>
                {countText !== null && (
                  <div className="flex items-center gap-1 shrink-0 pt-0.5">
                    <span
                      className={`text-xs whitespace-nowrap ${
                        isZeroFieldSigner ? 'text-amber-700' : 'text-gray-500'
                      }`}
                    >
                      {countText}
                    </span>
                    {showAmber && (
                      <AlertCircle className="w-3.5 h-3.5 text-amber-500 shrink-0" />
                    )}
                  </div>
                )}
              </button>
            );
          })}
        </div>
      </div>

      {/* Field type buttons */}
      <div>
        <label className="block text-xs font-medium text-gray-500 uppercase tracking-wide mb-1.5">
          Field type
        </label>
        <div className="space-y-1.5">
          {FIELD_KINDS.map(({ kind, label, Icon }) => {
            const isActive = activeFieldKind === kind;
            return (
              <button
                key={kind}
                type="button"
                onClick={() => onFieldKindChange(isActive ? null : kind)}
                className={`w-full flex items-center gap-3 px-3 py-2.5 text-sm rounded-lg border-2 transition
                  ${
                    isActive
                      ? 'bg-white font-medium'
                      : 'bg-white border-gray-200 text-gray-700 hover:border-gray-300 hover:bg-gray-50'
                  }`}
                style={
                  isActive
                    ? { borderColor: activeColor, color: activeColor }
                    : undefined
                }
              >
                <Icon className="w-4 h-4 shrink-0" />
                {label}
              </button>
            );
          })}
        </div>
      </div>

      <p className="text-xs text-gray-400 leading-relaxed">
        Select a field type, then click on the document to place it. Drag to
        reposition, or use the corner handle to resize.
      </p>
    </div>
  );
}
